// packages/shared/api-client/endpoints/documents.js
// Document library API endpoints

import { baseApi } from "../baseApi";

/**
 * Document API endpoints
 * Provides document management with full-text search, upload, and categorization
 */
const documentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get/browse documents with filters
    getDocuments: builder.query({
      query: (params = {}) => ({
        url: '/api/documents',
        params, // { category?, department?, page?, limit? }
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Document', id })),
              { type: 'Document', id: 'LIST' },
            ]
          : [{ type: 'Document', id: 'LIST' }],
    }),

    // Full-text search documents
    searchDocuments: builder.query({
      query: (params = {}) => ({
        url: '/api/documents/search',
        params, // { q, category?, department?, page?, limit? }
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Document', id })),
              { type: 'Document', id: 'SEARCH' },
            ]
          : [{ type: 'Document', id: 'SEARCH' }],
    }),

    // Get available document categories
    getDocumentCategories: builder.query({
      query: () => '/api/documents/categories',
      providesTags: ['Document'],
    }),

    // Get specific document by ID
    getDocumentById: builder.query({
      query: (id) => `/api/documents/${id}`,
      providesTags: (result, error, id) => [{ type: 'Document', id }],
    }),

    // Upload document
    uploadDocument: builder.mutation({
      query: (documentData) => ({
        url: '/api/documents',
        method: 'POST',
        body: documentData, // { title, description, fileName, fileType, fileSize, category, departmentId? }
      }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),

    // Update document metadata
    updateDocument: builder.mutation({
      query: ({ id, ...documentData }) => ({
        url: `/api/documents/${id}`,
        method: 'PUT',
        body: documentData, // { title?, description?, category?, departmentId? }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Document', id },
        { type: 'Document', id: 'LIST' },
      ],
    }),

    // Delete document
    deleteDocument: builder.mutation({
      query: (id) => ({
        url: `/api/documents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),

    // View document in browser (get signed S3 URL for inline viewing)
    viewDocument: builder.mutation({
      query: (id) => ({
        url: `/api/documents/${id}/view`,
        method: 'GET',
      }),
    }),

    // Download document (get signed S3 URL for download)
    downloadDocument: builder.mutation({
      query: (id) => ({
        url: `/api/documents/${id}/download`,
        method: 'GET',
      }),
    }),

    // Process uploaded document (extract PDF text)
    processDocument: builder.mutation({
      query: (id) => ({
        url: `/api/documents/${id}/process`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Document', id }],
    }),
  }),
});

// Export hooks for usage in components
export const useGetDocumentsQuery = documentApi.useGetDocumentsQuery;
export const useLazyGetDocumentsQuery = documentApi.useLazyGetDocumentsQuery;
export const useSearchDocumentsQuery = documentApi.useSearchDocumentsQuery;
export const useGetDocumentCategoriesQuery = documentApi.useGetDocumentCategoriesQuery;
export const useGetDocumentByIdQuery = documentApi.useGetDocumentByIdQuery;
export const useUploadDocumentMutation = documentApi.useUploadDocumentMutation;
export const useUpdateDocumentMutation = documentApi.useUpdateDocumentMutation;
export const useDeleteDocumentMutation = documentApi.useDeleteDocumentMutation;
export const useViewDocumentMutation = documentApi.useViewDocumentMutation;
export const useDownloadDocumentMutation = documentApi.useDownloadDocumentMutation;
export const useProcessDocumentMutation = documentApi.useProcessDocumentMutation;
